import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../lib/api-client'
import { useActions } from '../../hooks/useCompetitiveData'

interface ActionItemListProps {
  competitorId?: number
}

export default function ActionItemList({ competitorId }: ActionItemListProps) {
  const [showCompleted, setShowCompleted] = useState(false)
  const queryClient = useQueryClient()
  const { data, isLoading, error } = useActions(competitorId)

  const actions = data?.actions ?? []
  const pendingItems = useMemo(
    () =>
      actions.filter((action) => {
        const status = String(action.status ?? '').toLowerCase()
        return status !== 'done' && status !== 'completed'
      }),
    [actions]
  )
  const completedItems = useMemo(
    () =>
      actions.filter((action) => {
        const status = String(action.status ?? '').toLowerCase()
        return status === 'done' || status === 'completed'
      }),
    [actions]
  )

  const updateActionMutation = useMutation({
    mutationFn: async (payload: { id: number; status: string }) =>
      apiClient.patch(`/competitive/actions/${payload.id}`, { status: payload.status }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['competitive', 'actions'] })
    },
  })

  const errorMessage = error instanceof Error ? error.message : null

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Action Items</h3>

      <p className="mt-2 text-sm text-slate-600">
        {pendingItems.length} pending • {completedItems.length} completed
      </p>

      {errorMessage && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{errorMessage}</div>
      )}

      {isLoading ? (
        <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">Loading action items...</div>
      ) : actions.length === 0 ? (
        <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
          No action items yet — check back after the next daily brief.
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {pendingItems.map((action) => (
            <label
              key={action.id}
              className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
            >
              <input
                type="checkbox"
                checked={false}
                disabled={updateActionMutation.isPending}
                onChange={() => updateActionMutation.mutate({ id: action.id, status: 'done' })}
                className="h-4 w-4 rounded border-slate-300 text-[var(--patterniq-navy-900)] focus:ring-blue-500"
              />
              <span>{action.action_text}</span>
            </label>
          ))}

          {completedItems.length > 0 && (
            <div className="pt-2">
              <button
                type="button"
                onClick={() => setShowCompleted((current) => !current)}
                className="text-xs font-semibold text-[var(--patterniq-navy-900)] hover:underline"
              >
                {showCompleted ? 'Hide' : 'Show'} {completedItems.length} completed
              </button>

              {showCompleted && (
                <div className="mt-2 space-y-2">
                  {completedItems.map((action) => (
                    <div
                      key={action.id}
                      className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
                    >
                      {action.action_text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  )
}
